import { countryCodeToFlagEmoji } from './country';
import { getUserLikelyCountry } from './geo';

// Maps common ISO 4217 currency codes to a representative ISO 3166-1 alpha-2 country code,
// so we can reuse countryCodeToFlagEmoji to render a flag for a currency dropdown option.
// EUR maps to 'EU' since the regional indicator trick also renders a valid EU flag emoji.
const CURRENCY_TO_COUNTRY_CODE: Record<string, string> = {
  AUD: 'AU',
  CAD: 'CA',
  CHF: 'CH',
  DKK: 'DK',
  EUR: 'EU',
  GBP: 'GB',
  JPY: 'JP',
  NOK: 'NO',
  NZD: 'NZ',
  SEK: 'SE',
  USD: 'US',
};

// Reverse of CURRENCY_TO_COUNTRY_CODE, used to map a visitor's country to their local currency.
// The EUR -> 'EU' entry inverts harmlessly since 'EU' is never a real visitor country code.
const COUNTRY_CODE_TO_CURRENCY: Record<string, string> = Object.fromEntries(
  Object.entries(CURRENCY_TO_COUNTRY_CODE).map(([currency, countryCode]) => [
    countryCode,
    currency,
  ]),
);

// Locale to format each currency in, chosen to match how people who primarily use that currency
// expect to see it displayed (symbol position and spacing) - i.e. Intl.NumberFormat's output for
// that locale, rather than a fixed locale like 'en' for every currency. Where a currency spans
// multiple language regions with different conventions (EUR, CHF, CAD), the locale used by the
// majority of that currency's users is picked (e.g. CHF -> German-speaking Switzerland's 'CHF 10',
// not French-speaking Switzerland's '10 CHF').
const CURRENCY_TO_LOCALE: Record<string, string> = {
  AUD: 'en-AU',
  CAD: 'en-CA',
  CHF: 'de-CH',
  DKK: 'da-DK',
  EUR: 'de-DE',
  GBP: 'en-GB',
  JPY: 'ja-JP',
  NOK: 'nb-NO',
  NZD: 'en-NZ',
  SEK: 'sv-SE',
  USD: 'en-US',
};

// Overrides for currencies where the CLDR symbol for CURRENCY_TO_LOCALE doesn't match the desired
// display: CHF's 'de-CH' symbol is the code itself ('CHF'), but 'Fr.' is the conventional Swiss
// franc symbol; DKK's 'kr.' is standardised to 'kr' to match NOK and SEK.
const CURRENCY_SYMBOL_OVERRIDE: Record<string, string> = {
  CHF: 'Fr.',
  DKK: 'kr',
};

// ISO 3166-1 alpha-2 codes for current EU member states, used to default their currency to EUR
const EU_COUNTRY_CODES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

type AltListItem = { selected: boolean; value: string };

type EngagingNetworksData = {
  altLists?: { id: number; data: { name: string; data: AltListItem[] }[] }[];
  dependencies?: {
    conditions: { comparator: string; value: string }[];
    actions: { type: string; target?: string | number; altlist?: string }[];
  }[];
};

/**
 * Find the donation amount list EN's field update swaps in for currencyCode, from the field
 * update settings EN puts in window.EngagingNetworks
 */
const getDonationAmountListForCurrency = (currencyCode: string): AltListItem[] | undefined => {
  const en = (window as unknown as { EngagingNetworks?: EngagingNetworksData }).EngagingNetworks;

  if (!en?.altLists || !en.dependencies) return undefined;

  for (const dependency of en.dependencies) {
    const matchesCurrency = dependency.conditions.some(
      (condition) => condition.comparator === '==' && condition.value === currencyCode,
    );

    if (!matchesCurrency) continue;

    for (const action of dependency.actions) {
      if (action.type !== 'altlist') continue;

      // EN gives the target as a string on some pages and a number on others
      const lists = en.altLists.find(
        (altList) => String(altList.id) === String(action.target),
      )?.data;
      const items = lists?.find((list) => list.name === action.altlist)?.data;

      // Only the donation amount lists have an 'Other' option (this skips e.g. the US/Canada
      // state lists)
      if (items?.some((item) => item.value === 'Other')) return items;
    }
  }

  return undefined;
};

/**
 * After a currency change, select the new currency's default donation amount (as set in EN's
 * field update settings) once EN has swapped in the new amount list. EN swaps the list but
 * doesn't apply its default, instead keeping the previous amount or falling back to 'Other'.
 */
export const selectDefaultDonationAmountOnCurrencyChange = (): void => {
  const donationAmountField = document.querySelector('.en__field--donationAmt');

  if (!donationAmountField) return;

  let pendingList: AltListItem[] | undefined;

  const applyPendingDefault = () => {
    if (!pendingList) return;

    const currentValues = Array.from(
      document.querySelectorAll<HTMLInputElement>('[name="transaction.donationAmt"]'),
    ).map((radioButton) => radioButton.value);

    // Wait until EN has actually swapped in the new list
    if (currentValues.join() !== pendingList.map((item) => item.value).join()) return;

    const defaultValue = pendingList.find((item) => item.selected)?.value;

    pendingList = undefined;

    if (!defaultValue) return;

    const radioButton = document.querySelector<HTMLInputElement>(
      `[name="transaction.donationAmt"][value="${defaultValue}"]`,
    );

    if (!radioButton) return;

    radioButton.checked = true;
    radioButton.dispatchEvent(new Event('change', { bubbles: true }));
  };

  document.addEventListener('change', (event) => {
    const target = event.target;

    if (
      !(target instanceof HTMLSelectElement || target instanceof HTMLInputElement) ||
      target.name !== 'transaction.paycurrency'
    )
      return;

    pendingList = getDonationAmountListForCurrency(target.value);
  });

  // EN swaps in the new list after a delay, so apply the default once the buttons change
  new MutationObserver(applyPendingDefault).observe(donationAmountField, {
    childList: true,
    subtree: true,
  });
};

/**
 * Prefix each currency dropdown option's label with its flag emoji
 */
export const addFlagEmojiToCurrencyOptions = () => {
  const currencyField = getCurrencyField();

  if (!currencyField || !(currencyField instanceof HTMLSelectElement)) return;

  const currencySelect = currencyField as HTMLSelectElement;

  Array.from(currencySelect.options).forEach((option) => {
    if (!option.value) return;

    const flagEmoji = currencyCodeToFlagEmoji(option.value);

    if (flagEmoji) option.text = `${flagEmoji} ${option.text}`;
  });
};

/**
 * Auto-sets the currency field (if present) based on the visitor's likely country.
 */
export const autoSetCurrencyField = async (): Promise<void> => {
  const currencyField = getCurrencyField();

  if (!currencyField) return;

  const likelyCountry = await getUserLikelyCountry();

  if (!likelyCountry) return;

  const currency = getCurrencyForCountry(likelyCountry);

  console.log(`Auto-setting currency field to ${currency} for country ${likelyCountry}`);

  if (currencyField instanceof HTMLSelectElement) {
    const optionExists = Array.from(currencyField.options).some(
      (option) => option.value === currency,
    );

    if (!optionExists) return;
  }

  currencyField.value = currency;
  currencyField.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * Convert an ISO 4217 currency code (e.g. 'GBP') into a flag emoji, if a mapping exists
 */
export const currencyCodeToFlagEmoji = (code: string): string | undefined => {
  const countryCode = CURRENCY_TO_COUNTRY_CODE[code.toUpperCase()];

  return countryCode ? countryCodeToFlagEmoji(countryCode) : undefined;
};

/**
 * Determine how to display an ISO 4217 currency code (e.g. 'SEK'): its symbol (e.g. 'kr'), any
 * separator between the symbol and the amount, whether the symbol goes after the amount rather
 * than before, and the decimal separator (e.g. ',' for EUR) to use for non-round amounts. Formats
 * in the locale from CURRENCY_TO_LOCALE so the result matches local convention (e.g. '£10' but
 * '10 kr'), falling back to the code itself if it can't be determined.
 */
const getCurrencyDisplay = (
  code: string,
): { decimalSeparator: string; separator: string; suffix: boolean; symbol: string } => {
  const upperCode = code.toUpperCase();

  try {
    const parts = new Intl.NumberFormat(CURRENCY_TO_LOCALE[upperCode] ?? 'en', {
      currency: upperCode,
      currencyDisplay: 'narrowSymbol',
      style: 'currency',
    }).formatToParts(0);

    const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value ?? '.';
    const currencyIndex = parts.findIndex((part) => part.type === 'currency');

    if (currencyIndex === -1)
      return { decimalSeparator, separator: '', suffix: false, symbol: upperCode };

    const suffix = currencyIndex > 0;
    const separatorPart = parts[suffix ? currencyIndex - 1 : currencyIndex + 1];
    const separator = separatorPart?.type === 'literal' ? separatorPart.value : '';
    const symbol = CURRENCY_SYMBOL_OVERRIDE[upperCode] ?? parts[currencyIndex].value;

    return { decimalSeparator, separator, suffix, symbol };
  } catch {
    return { decimalSeparator: '.', separator: '', suffix: false, symbol: upperCode };
  }
};

/**
 * Determine the likely currency for a given ISO 3166-1 alpha-2 country code, defaulting to Euro.
 */
const getCurrencyForCountry = (countryCode: string): string => {
  const code = countryCode.toUpperCase();

  if (COUNTRY_CODE_TO_CURRENCY[code]) return COUNTRY_CODE_TO_CURRENCY[code];
  if (EU_COUNTRY_CODES.includes(code)) return 'EUR';

  return 'EUR';
};

/**
 * Get the transaction currency select field, if present on the page
 */
export const getCurrencyField = (): HTMLInputElement | HTMLSelectElement | undefined => {
  const currencyField = document.querySelector('[name="transaction.paycurrency"]');

  return (currencyField as HTMLInputElement | HTMLSelectElement) || undefined;
};

/**
 * Keep the donation amount labels (e.g. '£50') in sync with the currency field: updates them
 * once on load, then again on every subsequent change (including programmatic changes from
 * autoSetCurrencyField, which dispatches a 'change' event after setting the field's value).
 */
export const syncDonationAmountLabelsWithCurrency = (): void => {
  const currencyField = getCurrencyField();

  if (!currencyField) return;

  updateDonationAmountLabelsForCurrency(currencyField.value);

  currencyField.addEventListener('change', () =>
    setTimeout(() => updateDonationAmountLabelsForCurrency(currencyField.value), 0),
  );
};

/**
 * Replace the currency symbol in each donation amount label with the one for currencyCode,
 * positioned before or after the amount to match local convention (e.g. '£50' but '50 kr'), using
 * the locale's decimal separator for non-round amounts (e.g. '12,50 €'), and update the
 * '--currency-symbol' CSS custom property (used by the 'Other' amount input's ::before, which is
 * a fixed prefix box, so it always shows just the symbol) to match. Labels without a digit (e.g.
 * an 'Other' amount option) are left untouched.
 */
const updateDonationAmountLabelsForCurrency = (currencyCode: string): void => {
  const { decimalSeparator, separator, suffix, symbol } = getCurrencyDisplay(currencyCode);

  document
    .querySelectorAll<HTMLElement>('.en__field--donationAmt .en__field__element label')
    .forEach((label) => {
      // Cache the amount (digits only) and reformat from that, rather than re-parsing our own
      // formatted text on every change. But if the label's text no longer matches what we last
      // wrote, EN has swapped in a new amount list (e.g. after a currency change), so re-read it.
      if (label.dataset.amount === undefined || label.textContent !== label.dataset.rendered) {
        // Must start with a digit, so a symbol like 'Fr.' can't be mistaken for the amount
        const rawAmount = label.textContent?.match(/\d[\d.,]*/)?.[0];

        if (!rawAmount) return;

        label.dataset.amount = rawAmount;
      }

      const rawAmount = label.dataset.amount;

      if (!rawAmount) return;

      const amount = rawAmount.includes('.')
        ? rawAmount.replace('.', decimalSeparator)
        : rawAmount;

      label.textContent = suffix
        ? `${amount}${separator}${symbol}`
        : `${symbol}${separator}${amount}`;

      label.dataset.rendered = label.textContent;
    });

  document
    .querySelectorAll<HTMLElement>('.en__field--donationAmt .en__field__item--other')
    .forEach((item) => item.style.setProperty('--currency-symbol', JSON.stringify(symbol)));
};
